/** SPARQL prefix helpers (spec §15.1): detect used-but-undeclared prefixes, expand prefixed names. Pure. */

const IRI_RE = /<[^<>"{}|^`\\\s]*>/g;
const STRING_RE = /"""[\s\S]*?"""|'''[\s\S]*?'''|"(?:[^"\\\n]|\\.)*"|'(?:[^'\\\n]|\\.)*'/g;
const COMMENT_RE = /#[^\n]*/g;
const DECL_RE = /\bPREFIX\s+([A-Za-z][\w-]*|):\s*<([^>]*)>/gi;
const PNAME_RE = /(^|[^\w?$:.-])([A-Za-z][\w-]*):(?=[A-Za-z_0-9]|[\s;,.)}\]]|$)/g;

/** Prefixes declared in the query text: prefix → namespace. */
export function declaredPrefixes(query: string): Record<string, string> {
  const out: Record<string, string> = {};
  for (const m of query.matchAll(DECL_RE)) out[m[1]!] = m[2]!;
  return out;
}

/** Strip IRIs, strings and comments so that `http:` or `a:b` inside them never counts as usage. */
function code(query: string): string {
  return query.replace(IRI_RE, " ").replace(STRING_RE, " ").replace(COMMENT_RE, " ").replace(/\bPREFIX\s+[A-Za-z][\w-]*:/gi, " ");
}

export function usedPrefixes(query: string): Set<string> {
  const out = new Set<string>();
  for (const m of code(query).matchAll(PNAME_RE)) {
    if (m[2] && m[2] !== "_") out.add(m[2]);
  }
  return out;
}

/** `PREFIX` lines for prefixes used in the query, not declared, and known to the server. */
export function missingPrefixDeclarations(query: string, known: Record<string, string>): string[] {
  const declared = declaredPrefixes(query);
  return [...usedPrefixes(query)]
    .filter((p) => !(p in declared) && p in known)
    .sort()
    .map((p) => `PREFIX ${p}: <${known[p]}>`);
}

/** The query with missing declarations inserted at the top ("Insert prefixes" command). */
export function withMissingPrefixes(query: string, known: Record<string, string>): string {
  const lines = missingPrefixDeclarations(query, known);
  return lines.length ? `${lines.join("\n")}\n${query}` : query;
}

/** `<iri>` or `prefix:local` → full IRI, using the query's own declarations first. */
export function expandTerm(token: string, query: string, known: Record<string, string>): string | null {
  if (token.startsWith("<") && token.endsWith(">")) return token.slice(1, -1);
  const i = token.indexOf(":");
  if (i <= 0) return null;
  const prefix = token.slice(0, i);
  const ns = declaredPrefixes(query)[prefix] ?? known[prefix];
  return ns ? ns + token.slice(i + 1) : null;
}

/** Shorten an IRI with the known prefixes (for completion insertions). */
export function compact(iri: string, prefixes: Record<string, string>): string {
  let best: [string, string] | null = null;
  for (const [p, ns] of Object.entries(prefixes)) {
    if (iri.startsWith(ns) && /^[A-Za-z_0-9][\w.-]*$/.test(iri.slice(ns.length)) && (!best || ns.length > best[1].length)) best = [p, ns];
  }
  return best ? `${best[0]}:${iri.slice(best[1].length)}` : `<${iri}>`;
}
